/**
 * R75b · Settings → System → Scheduler
 * ----------------------------------------------------------------
 * Super-admin-only ops page listing every APScheduler job with:
 *   • pause / resume switch (calls the admin API)
 *   • last-run duration + errors so slow jobs bubble to the top
 *   • live system metrics (RSS, FDs, event-loop lag, CPU%)
 *   • R76 · Priority tier badges + per-workspace AI-cron toggle matrix
 *
 * All data comes from:
 *   GET  /api/admin/scheduler/status
 *   POST /api/admin/scheduler/pause/{id}
 *   POST /api/admin/scheduler/resume/{id}
 *   GET  /api/admin/metrics
 *   GET  /api/admin/scheduler/workspace-toggles
 *   PUT  /api/admin/scheduler/workspace-toggles/{ws}/{cron_key}
 *   POST /api/admin/scheduler/enforce-stagger
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ROOT_API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Loader2, RotateCw, AlertTriangle, Shuffle, PauseCircle, PlayCircle } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { apiErrorMessage } from "@/lib/apiError";

// Scheduler admin endpoints live under /api (not /api/v1) — build a
// dedicated axios client with the correct baseURL + auth header so we
// don't hit 404s.
const rootApi = axios.create({ baseURL: ROOT_API });
rootApi.interceptors.request.use((cfg) => {
  const token = localStorage.getItem("co_token");
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

const PRIO_STYLE = {
  critical: "bg-rose-600 text-white",
  weekly: "bg-indigo-600 text-white",
  nice: "bg-slate-500 text-white",
};

export default function SchedulerSettings() {
  const [jobs, setJobs] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [tab, setTab] = useState("running");
  const [staggerBusy, setStaggerBusy] = useState(false);
  // Feb 2026 R282 · Master ON/OFF flag + lease info from the /status
  // response. Persists across restarts via `system_settings.scheduler`.
  const [masterPaused, setMasterPaused] = useState(false);
  const [masterMeta, setMasterMeta] = useState({});
  const [lease, setLease] = useState(null);
  const [masterBusy, setMasterBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [st, mt] = await Promise.all([
        rootApi.get("/admin/scheduler/status"),
        rootApi.get("/admin/metrics"),
      ]);
      setJobs(st.data?.jobs || []);
      setMasterPaused(!!st.data?.master_paused);
      setMasterMeta({
        at: st.data?.master_paused_at,
        by: st.data?.master_paused_by,
      });
      setLease(st.data?.lease || null);
      setMetrics(mt.data || null);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load scheduler")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, []);

  const toggleMaster = async () => {
    const goingOff = !masterPaused;   // true = about to pause
    if (goingOff && !window.confirm("Master-pause the entire scheduler? Every cron (digests, reminders, recurring invoices, sequences, sweeps) stops firing. Persists across restarts until you resume.")) return;
    setMasterBusy(true);
    try {
      const path = goingOff ? "/admin/scheduler/master-pause" : "/admin/scheduler/master-resume";
      const r = await rootApi.post(path);
      toast.success(goingOff
        ? `Master paused — ${r.data?.count || 0} job(s) stopped`
        : `Master resumed — ${r.data?.count || 0} job(s) restarted`);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Master toggle failed")); }
    finally { setMasterBusy(false); }
  };

  const toggle = async (job) => {
    setBusyId(job.id);
    try {
      const action = job.paused ? "resume" : "pause";
      await rootApi.post(`/admin/scheduler/${action}/${encodeURIComponent(job.id)}`);
      toast.success(`Job ${job.id} ${action}d`);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Toggle failed")); }
    finally { setBusyId(null); }
  };

  const runStagger = async () => {
    setStaggerBusy(true);
    try {
      const r = await rootApi.post("/admin/scheduler/enforce-stagger");
      const n = (r.data?.moves || []).length;
      toast.success(n === 0
        ? "Stagger check ok — no jobs needed reshuffling"
        : `Rebalanced ${n} job(s) to spread load across minutes`);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Stagger failed")); }
    finally { setStaggerBusy(false); }
  };

  // R96 · Bulk pause / resume every scheduler job. Filters (priority /
  // heavy_ai_only / errored_only) are applied when the current tab implies
  // one — e.g. clicking "Pause all" while the "Heavy AI" tab is active
  // targets only heavy AI crons. Otherwise every job in view is toggled.
  const [bulkBusy, setBulkBusy] = useState(false);
  const bulkFilterFromTab = () => {
    if (tab === "critical") return { priority: "critical" };
    if (tab === "weekly")   return { priority: "weekly" };
    if (tab === "heavy")    return { heavy_ai_only: true };
    if (tab === "errored")  return { errored_only: true };
    return {};
  };
  const bulkPause = async () => {
    const params = bulkFilterFromTab();
    const scope = Object.keys(params).length ? `filtered (${tab})` : "every running job";
    if (!window.confirm(`Pause ${scope}? You can resume individually or in bulk any time.`)) return;
    setBulkBusy(true);
    try {
      const r = await rootApi.post("/admin/scheduler/pause-all", null, { params });
      toast.success(`Paused ${r.data?.count || 0} job(s)`);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Bulk pause failed")); }
    finally { setBulkBusy(false); }
  };
  const bulkResume = async () => {
    const params = bulkFilterFromTab();
    setBulkBusy(true);
    try {
      const r = await rootApi.post("/admin/scheduler/resume-all", null, { params });
      toast.success(`Resumed ${r.data?.count || 0} job(s)`);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Bulk resume failed")); }
    finally { setBulkBusy(false); }
  };

  const visible = useMemo(() => {
    let list = jobs;
    if (tab === "running") list = list.filter((j) => !j.paused);
    else if (tab === "paused") list = list.filter((j) => j.paused);
    else if (tab === "errored") list = list.filter((j) => (j.errors || 0) > 0);
    else if (tab === "critical") list = list.filter((j) => j.priority === "critical");
    else if (tab === "weekly") list = list.filter((j) => j.priority === "weekly");
    else if (tab === "heavy") list = list.filter((j) => !!j.heavy_cron_key);
    if (q.trim()) {
      const qq = q.trim().toLowerCase();
      list = list.filter((j) => (j.id || "").toLowerCase().includes(qq) || (j.name || "").toLowerCase().includes(qq));
    }
    return list;
  }, [jobs, tab, q]);

  return (
    <div className="space-y-4 p-4" data-testid="scheduler-settings-page">
      <PageHeader title="Scheduler" description="Live APScheduler jobs · super-admin ops control" />

      {/* Feb 2026 R282 · Master ON/OFF toggle + leader lease. Persistent
          across restarts via `system_settings.scheduler`. */}
      <div
        className={`border-2 ${masterPaused ? "border-rose-500 bg-rose-50" : "border-emerald-500 bg-emerald-50"} p-4 flex items-center gap-4`}
        data-testid="scheduler-master-card"
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <span
            className={`relative flex h-3 w-3 shrink-0`}
            aria-hidden
          >
            {!masterPaused && (
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
            )}
            <span className={`relative inline-flex rounded-full h-3 w-3 ${masterPaused ? "bg-rose-600" : "bg-emerald-600"}`} />
          </span>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <div className="text-sm font-bold uppercase tracking-widest"
                     data-testid="scheduler-master-state">
                {masterPaused ? "Scheduler OFF" : "Scheduler ON"}
              </div>
              {lease && (
                <span
                  className="text-[10px] font-mono border border-border bg-white px-1.5 py-0.5 truncate max-w-[280px]"
                  title={`Leader pod: ${lease.holder || "—"}${lease.acquired_at ? ` since ${lease.acquired_at}` : ""}`}
                  data-testid="scheduler-lease-chip"
                >
                  {lease.is_leader ? "🟢 this pod is leader" :
                    lease.holder ? `leader: ${String(lease.holder).slice(-16)}…` :
                    "no leader yet"}
                </span>
              )}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {masterPaused
                ? <>All crons are paused. {masterMeta.by ? <>Turned off by <b>{masterMeta.by}</b> {masterMeta.at ? `at ${new Date(masterMeta.at).toLocaleString()}` : ""}.</> : "Flag was persisted so the state survives restarts."}</>
                : "Digests, reminders, recurring invoices, sequences and sweeps are running normally."}
            </div>
          </div>
        </div>
        <Button
          onClick={toggleMaster}
          disabled={masterBusy}
          className={`rounded-none shrink-0 ${masterPaused
            ? "bg-emerald-600 hover:bg-emerald-700 text-white"
            : "bg-rose-600 hover:bg-rose-700 text-white"}`}
          data-testid="scheduler-master-toggle"
        >
          {masterBusy
            ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            : masterPaused
              ? <PlayCircle className="w-4 h-4 mr-2" />
              : <PauseCircle className="w-4 h-4 mr-2" />}
          {masterPaused ? "Turn ON" : "Turn OFF"}
        </Button>
      </div>

      {metrics && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2" data-testid="scheduler-metrics-strip">
          <MetricTile label="RSS memory" value={metrics.memory?.rss_mb ? `${metrics.memory.rss_mb} MB` : "—"} />
          <MetricTile label="System avail" value={metrics.system?.percent != null ? `${metrics.system.percent}%` : "—"} tone={metrics.system?.percent > 85 ? "warn" : "ok"} />
          <MetricTile label="CPU %" value={`${metrics.cpu_percent ?? "—"}`} tone={metrics.cpu_percent > 80 ? "warn" : "ok"} />
          <MetricTile label="Open FDs" value={metrics.open_fds ?? "—"} />
          <MetricTile label="Loop lag" value={metrics.event_loop_lag_ms != null ? `${metrics.event_loop_lag_ms} ms` : "—"} tone={metrics.event_loop_lag_ms > 50 ? "warn" : "ok"} />
          <MetricTile label="Threads" value={metrics.threads ?? "—"} />
        </div>
      )}
      <div className="flex items-center gap-2 flex-wrap">
        <Input
          placeholder="Search job id or name…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="h-8 text-xs rounded-none w-full sm:w-64"
          data-testid="scheduler-search"
        />
        {[
          { v: "running",  label: `Running (${jobs.filter((j) => !j.paused).length})` },
          { v: "paused",   label: `Paused (${jobs.filter((j) => j.paused).length})` },
          { v: "errored",  label: `Errored (${jobs.filter((j) => (j.errors || 0) > 0).length})` },
          { v: "critical", label: `Critical (${jobs.filter((j) => j.priority === "critical").length})` },
          { v: "weekly",   label: `Weekly (${jobs.filter((j) => j.priority === "weekly").length})` },
          { v: "heavy",    label: `Heavy AI (${jobs.filter((j) => !!j.heavy_cron_key).length})` },
          { v: "all",      label: `All (${jobs.length})` },
        ].map((t) => (
          <button
            key={t.v}
            type="button"
            onClick={() => setTab(t.v)}
            className={`h-8 px-2 border text-[10px] uppercase tracking-widest ${tab === t.v ? "bg-primary text-primary-foreground border-primary" : "border-border bg-card hover:bg-muted/40"}`}
            data-testid={`scheduler-tab-${t.v}`}
          >{t.label}</button>
        ))}
        <Button
          size="sm"
          variant="outline"
          className="rounded-none h-8"
          onClick={bulkPause}
          disabled={bulkBusy}
          title="Pause every job in the current filter (or every running job when All tab is active)"
          data-testid="scheduler-bulk-pause"
        >
          {bulkBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <PauseCircle className="w-3.5 h-3.5 mr-1" />}
          Pause all
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="rounded-none h-8"
          onClick={bulkResume}
          disabled={bulkBusy}
          title="Resume every paused job in the current filter"
          data-testid="scheduler-bulk-resume"
        >
          {bulkBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <PlayCircle className="w-3.5 h-3.5 mr-1" />}
          Resume all
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="rounded-none h-8"
          onClick={runStagger}
          disabled={staggerBusy}
          title="Reshuffle cron minutes so no more than 3 jobs fire per minute"
          data-testid="scheduler-run-stagger"
        >
          {staggerBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Shuffle className="w-3.5 h-3.5 mr-1" />}
          Restagger
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="rounded-none h-8 ml-auto"
          onClick={load}
          disabled={loading}
          data-testid="scheduler-refresh"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCw className="w-3.5 h-3.5" />}
        </Button>
      </div>
      {loading && jobs.length === 0 ? (
        <div className="text-xs text-muted-foreground py-6 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Loading…</div>
      ) : visible.length === 0 ? (
        <div className="text-xs text-muted-foreground py-8 italic text-center">No jobs match this filter.</div>
      ) : (
        <div className="border border-border overflow-x-auto" data-testid="scheduler-jobs-table">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-muted/30 border-b border-border">
                <th className="text-left px-2 py-2 uppercase tracking-widest text-[10px]">Job</th>
                <th className="text-left px-2 py-2 uppercase tracking-widest text-[10px]">Trigger</th>
                <th className="text-center px-2 py-2 uppercase tracking-widest text-[10px]">Tier</th>
                <th className="text-right px-2 py-2 uppercase tracking-widest text-[10px]">Next</th>
                <th className="text-right px-2 py-2 uppercase tracking-widest text-[10px]">Last</th>
                <th className="text-right px-2 py-2 uppercase tracking-widest text-[10px]">Duration</th>
                <th className="text-right px-2 py-2 uppercase tracking-widest text-[10px]">Runs</th>
                <th className="text-right px-2 py-2 uppercase tracking-widest text-[10px]">Errors</th>
                <th className="text-center px-2 py-2 uppercase tracking-widest text-[10px]">On</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((j) => (
                <tr key={j.id} className={`border-b border-border/60 ${j.errors > 0 ? "bg-rose-50/60" : ""}`} data-testid={`scheduler-row-${j.id}`}>
                  <td className="px-2 py-1.5">
                    <div className="font-medium truncate max-w-[240px]" title={j.id}>{j.id}</div>
                    {j.heavy_cron_key && (
                      <span className="inline-block mt-0.5 text-[9px] uppercase tracking-widest bg-amber-100 text-amber-900 border border-amber-300 px-1"
                            title="Heavy AI cron · per-workspace toggle available">
                        heavy · {j.heavy_cron_key}
                      </span>
                    )}
                    {j.last_error && <div className="text-[10px] text-rose-700 truncate max-w-[240px]" title={j.last_error}><AlertTriangle className="w-2.5 h-2.5 inline mr-1" />{j.last_error}</div>}
                  </td>
                  <td className="px-2 py-1.5 text-[10px] text-neutral-700 max-w-[220px] truncate" title={j.trigger}>{j.trigger}</td>
                  <td className="px-2 py-1.5 text-center">
                    <span
                      className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 ${PRIO_STYLE[j.priority] || "bg-slate-400 text-white"}`}
                      data-testid={`scheduler-priority-${j.id}`}
                    >{j.priority || "nice"}</span>
                  </td>
                  <td className="px-2 py-1.5 text-right text-[10px]">{fmtDate(j.next_run_time)}</td>
                  <td className="px-2 py-1.5 text-right text-[10px]">{fmtDate(j.last_run_at)}</td>
                  <td className={`px-2 py-1.5 text-right text-[10px] ${j.last_duration_ms > 5000 ? "text-rose-700 font-semibold" : ""}`}>{j.last_duration_ms != null ? `${j.last_duration_ms} ms` : "—"}</td>
                  <td className="px-2 py-1.5 text-right text-[10px]">{j.runs}</td>
                  <td className={`px-2 py-1.5 text-right text-[10px] ${j.errors > 0 ? "text-rose-700 font-semibold" : "text-neutral-700"}`}>{j.errors}</td>
                  <td className="px-2 py-1.5 text-center">
                    <Switch
                      checked={!j.paused}
                      onCheckedChange={() => toggle(j)}
                      disabled={busyId === j.id}
                      data-testid={`scheduler-toggle-${j.id}`}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-[10px] text-muted-foreground italic">
        Stats reset when the backend restarts. Auto-refreshes every 20 s. Tier ·
        <span className="mx-1 text-rose-600">critical</span>never delayed;
        <span className="mx-1 text-indigo-600">weekly</span>rebalanced when Monday slots crowd;
        <span className="ml-1 text-slate-600">nice</span>everything else.
      </p>

      <WorkspaceTogglesPanel />
    </div>
  );
}

function MetricTile({ label, value, tone = "ok" }) {
  const cls = tone === "warn" ? "border-amber-500 bg-amber-50 text-amber-900" : "border-border bg-card";
  return (
    <div className={`border p-2 ${cls}`}>
      <div className="text-[9px] uppercase tracking-widest text-neutral-700 font-semibold">{label}</div>
      <div className="text-sm font-semibold text-slate-900">{value}</div>
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const now = Date.now();
    const diff = d.getTime() - now;
    const abs = Math.abs(diff);
    const min = Math.floor(abs / 60000);
    const hr = Math.floor(min / 60);
    if (abs < 60000) return diff > 0 ? "<1m" : "now";
    if (abs < 3600000) return diff > 0 ? `in ${min}m` : `${min}m ago`;
    if (abs < 86400000) return diff > 0 ? `in ${hr}h` : `${hr}h ago`;
    return d.toISOString().slice(5, 16).replace("T", " ");
  } catch { return iso; }
}

// ─────────────────────────────────────────────────────────────
// R76 · Per-workspace toggle matrix for heavy AI crons.
// ─────────────────────────────────────────────────────────────

function WorkspaceTogglesPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);   // `${ws}::${key}`
  const [wsFilter, setWsFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await rootApi.get("/admin/scheduler/workspace-toggles");
      setData(r.data || null);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load workspace toggles")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const upsert = async (ws, key, enabled) => {
    setBusy(`${ws}::${key}`);
    try {
      await rootApi.put(
        `/admin/scheduler/workspace-toggles/${ws}/${key}`,
        { enabled });
      toast.success(`${enabled ? "Enabled" : "Disabled"} · ${key}`);
      // Optimistic update: mutate data locally so the switch flips immediately
      setData((d) => {
        if (!d) return d;
        const workspaces = d.workspaces.map((w) => {
          if (w.id !== ws) return w;
          return { ...w, toggles: { ...w.toggles, [key]: enabled } };
        });
        return { ...d, workspaces };
      });
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to update toggle"));
    } finally {
      setBusy(null);
    }
  };

  const workspaces = useMemo(() => {
    if (!data?.workspaces) return [];
    const q = wsFilter.trim().toLowerCase();
    if (!q) return data.workspaces;
    return data.workspaces.filter((w) =>
      (w.name || "").toLowerCase().includes(q) ||
      (w.id || "").toLowerCase().includes(q)
    );
  }, [data, wsFilter]);

  if (loading && !data) {
    return (
      <div className="border border-border p-4 mt-4" data-testid="scheduler-workspace-toggles">
        <div className="text-xs text-muted-foreground flex items-center gap-2">
          <Loader2 className="w-3 h-3 animate-spin" /> Loading workspace toggles…
        </div>
      </div>
    );
  }
  if (!data) return null;
  const crons = data.crons || [];

  return (
    <div className="border border-border p-4 mt-6 space-y-3" data-testid="scheduler-workspace-toggles">
      <div className="flex items-center gap-2 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-neutral-700 font-semibold">Per-workspace heavy-AI toggles</div>
          <div className="text-[11px] text-muted-foreground">
            Turn off individual AI-heavy crons for a workspace without editing env vars.
            Default: enabled. Only workspaces explicitly opted-out appear disabled.
          </div>
        </div>
        <Input
          placeholder="Filter workspaces by name or id…"
          value={wsFilter}
          onChange={(e) => setWsFilter(e.target.value)}
          className="h-8 text-xs rounded-none w-full sm:w-64 ml-auto"
          data-testid="scheduler-workspace-toggles-filter"
        />
        <Button
          size="sm"
          variant="outline"
          className="rounded-none h-8"
          onClick={load}
          disabled={loading}
          data-testid="scheduler-workspace-toggles-refresh"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCw className="w-3.5 h-3.5" />}
        </Button>
      </div>
      <div className="overflow-x-auto border border-border">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-muted/30 border-b border-border">
              <th className="text-left px-2 py-2 uppercase tracking-widest text-[10px] sticky left-0 bg-muted/30">Workspace</th>
              {crons.map((c) => (
                <th
                  key={c.key}
                  className="text-center px-2 py-2 uppercase tracking-widest text-[10px]"
                  title={c.description}
                >{c.key}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {workspaces.map((w) => (
              <tr
                key={w.id}
                className="border-b border-border/60"
                data-testid={`scheduler-workspace-row-${w.id}`}
              >
                <td className="px-2 py-1.5 sticky left-0 bg-card">
                  <div className="font-medium truncate max-w-[220px]" title={w.name}>{w.name}</div>
                  <div className="text-[9px] text-muted-foreground truncate max-w-[220px]" title={w.id}>{w.id.slice(0, 8)}…</div>
                </td>
                {crons.map((c) => {
                  const explicit = c.key in (w.toggles || {});
                  const enabled = explicit ? !!w.toggles[c.key] : true;
                  const key = `${w.id}::${c.key}`;
                  return (
                    <td key={c.key} className="px-2 py-1.5 text-center">
                      <Switch
                        checked={enabled}
                        onCheckedChange={(next) => upsert(w.id, c.key, next)}
                        disabled={busy === key}
                        data-testid={`scheduler-ws-toggle-${w.id}-${c.key}`}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-[10px] text-muted-foreground italic">
        Toggles take effect on the next scheduler tick — a job that&apos;s currently mid-run finishes normally.
      </div>
    </div>
  );
}
